/**
 * PortfolioPage — the multi-property cockpit (landing surface for 2+ property hosts).
 *
 * Real data end-to-end: rows are the user's properties (instant), commercial
 * metrics stream in from /app/portfolio-summary (30-day revenue + pace,
 * occupancy, blended reputation, next arrivals, 12-month revenue-by-property).
 * Anything without data renders as pending ("—") — never sample numbers.
 */

import { useMemo, useState } from "react"
import { useNavigate } from "react-router"
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import { ArrowRight, ChevronRight, Plus, Search, ListChecks, TrendingUp, TrendingDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePortfolioData, type PortfolioRow, type PortfolioStatus } from "./usePortfolioData"

const eyebrow = "text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground"

const STATUS_STYLE: Record<PortfolioStatus, { stripe: string; pillText: string; pillBg: string; label: string } | null> = {
  attention: { stripe: "bg-rose", pillText: "text-rose-strong", pillBg: "bg-rose-soft", label: "Needs attention" },
  watch: { stripe: "bg-amber", pillText: "text-amber-strong", pillBg: "bg-amber-soft", label: "Watch" },
  good: { stripe: "bg-brand", pillText: "text-brand-strong", pillBg: "bg-brand-soft", label: "On track" },
  neutral: null,
}

const fmtEur = (v: number) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v)

export function PortfolioPage() {
  const data = usePortfolioData()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return data.rows
    return data.rows.filter((r) => r.name.toLowerCase().includes(q) || r.address.toLowerCase().includes(q))
  }, [data.rows, search])

  const first = data.rows[0]
  const t = data.totals

  const kpis = t
    ? [
        { label: "Revenue · 30 days", value: t.revenue30d != null ? fmtEur(t.revenue30d) : "—", delta: t.revenueDeltaPct, note: `across ${data.propertyCount} propert${data.propertyCount === 1 ? "y" : "ies"}` },
        { label: "Avg occupancy", value: t.avgOccupancyPct != null ? `${t.avgOccupancyPct}%` : "—", delta: null, note: "last 30 days" },
        { label: "Avg reputation", value: t.avgReputation != null ? String(t.avgReputation) : "—", delta: null, note: "blended, all channels" },
        { label: "Open actions", value: String(t.openActions), delta: null, note: t.urgentActions > 0 ? `${t.urgentActions} urgent` : "across the portfolio", critical: t.urgentActions > 0 },
      ]
    : []

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
      {/* GREETING HERO */}
      <section
        className="relative mb-[18px] overflow-hidden rounded-card px-[30px] py-[26px] text-[#EAF3EE] shadow-sm"
        style={{ background: "radial-gradient(130% 150% at 100% 0%,#1c4c3b 0%,var(--sf-forest) 60%)" }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(46% 70% at 90% 116%,rgba(30,200,148,.2),transparent 70%)" }}
        />
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div className="min-w-[280px] flex-1">
            <div className="text-[12px] font-medium tracking-[0.02em] text-[#9dc2b3]">
              {data.dateLabel} · {data.greeting}{data.greetingName ? `, ${data.greetingName}` : ""}
            </div>
            <h2 className="mt-1.5 font-display text-[27px] font-bold leading-[1.1] tracking-[-0.02em]">
              {data.totalOpenActions > 0
                ? `${data.totalOpenActions} open action${data.totalOpenActions === 1 ? "" : "s"} across your portfolio`
                : "Your portfolio at a glance"}
            </h2>
            <p className="mt-2.5 max-w-[56ch] text-[14px] font-normal leading-[1.55] text-[#B9D0C6]">
              {data.totalOpenActions > 0
                ? "Work through what needs you in Tasks — everything else is running on its own."
                : data.metricsPending
                  ? "Pace, occupancy and reputation light up here as booking data syncs across your properties."
                  : "Everything is running clean across your properties."}
            </p>
            <div className="mt-[18px] flex flex-wrap gap-2.5">
              {first && (
                <button
                  type="button"
                  onClick={() => navigate(`/properties/${first.id}`)}
                  className="inline-flex items-center gap-2 rounded-[11px] bg-white px-4 py-2.5 text-[13px] font-semibold text-forest transition-opacity hover:opacity-90"
                  style={{ fontWeight: 650 }}
                >
                  Open {first.name}
                  <ArrowRight className="h-[15px] w-[15px]" strokeWidth={2} />
                </button>
              )}
              {first && (
                <button
                  type="button"
                  onClick={() => navigate(`/properties/${first.id}/tasks`)}
                  className="inline-flex items-center gap-2 rounded-[11px] border border-white/20 bg-white/[0.06] px-4 py-2.5 text-[13px] font-semibold text-[#EAF3EE] transition-colors hover:bg-white/[0.16]"
                >
                  <ListChecks className="h-[15px] w-[15px]" strokeWidth={1.8} />
                  See all tasks
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-none gap-[22px]">
            {t?.revenue30d != null ? (
              <div className="text-right">
                <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#7fb6a0]">Last 30 days</div>
                <div className="mt-1 font-display text-[30px] font-bold leading-none">{fmtEur(t.revenue30d)}</div>
                {t.revenueDeltaPct != null && (
                  <div className={cn("mt-[3px] inline-flex items-center gap-[3px] text-[12px] font-semibold", t.revenueDeltaPct >= 0 ? "text-[#9ff0c8]" : "text-[#f4b3a3]")}>
                    {t.revenueDeltaPct >= 0 ? <TrendingUp className="h-3 w-3" strokeWidth={2.4} /> : <TrendingDown className="h-3 w-3" strokeWidth={2.4} />}
                    {t.revenueDeltaPct >= 0 ? "+" : ""}{t.revenueDeltaPct}% vs prior 30d
                  </div>
                )}
              </div>
            ) : (
              <div className="text-right">
                <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#7fb6a0]">Portfolio</div>
                <div className="mt-1 font-display text-[30px] font-bold leading-none">{data.propertyCount}</div>
                <div className="mt-[3px] text-[12px] font-medium text-[#9dc2b3]">
                  propert{data.propertyCount === 1 ? "y" : "ies"}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* KPI STRIP */}
      {kpis.length > 0 && (
        <section className="mb-[18px] grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          {kpis.map((k) => (
            <article key={k.label} className="rounded-card border border-border bg-card px-[18px] py-4 shadow-sm">
              <div className="text-[12px] font-semibold text-muted-foreground">{k.label}</div>
              <div className="mt-2.5 flex items-baseline gap-2">
                <strong className="font-display text-[26px] font-bold tracking-[-0.02em] text-foreground">{k.value}</strong>
                {k.delta != null && (
                  <span className={cn("text-[12px] font-semibold", k.delta >= 0 ? "text-brand-strong" : "text-rose-strong")}>
                    {k.delta >= 0 ? "+" : ""}{k.delta}%
                  </span>
                )}
              </div>
              <p className={cn("mt-[7px] text-[11.5px] font-medium", (k as { critical?: boolean }).critical ? "text-rose-strong" : "text-muted-foreground")}>{k.note}</p>
            </article>
          ))}
        </section>
      )}

      {/* PROPERTIES */}
      <section className="mb-[18px]">
        <div className="mb-[13px] flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-[18px] font-bold tracking-[-0.02em] text-foreground">Properties</h2>
          <div className="flex items-center gap-2">
            {data.rows.length > 3 && (
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" strokeWidth={1.8} />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search properties…"
                  className="h-9 w-[220px] rounded-[10px] border border-border bg-card pl-8 pr-3 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate("/onboarding?mode=add")}
              className="inline-flex items-center gap-1.5 rounded-[10px] bg-forest px-3.5 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
              Add property
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {rows.length === 0 ? (
            <div className="rounded-card border border-dashed border-line-2 bg-card px-6 py-10 text-center text-[13px] text-muted-foreground">
              No properties match “{search}”.
            </div>
          ) : (
            rows.map((p) => <PropertyRowCard key={p.id} row={p} onOpen={() => navigate(`/properties/${p.id}`)} />)
          )}
        </div>

        {data.metricsPending && !data.loading && (
          <p className="mt-3 text-[11.5px] font-medium text-muted-foreground">
            Pace, occupancy and reputation appear per property as booking and review data sync.
          </p>
        )}
      </section>

      {/* REVENUE BY PROPERTY + ARRIVALS */}
      {(data.monthly.length > 0 || data.arrivals.length > 0) && (
        <section className={cn("grid grid-cols-1 gap-[18px]", data.monthly.length > 0 && data.arrivals.length > 0 && "lg:grid-cols-[1.5fr_1fr]")}>
          {data.monthly.length > 0 && (
            <div className="min-w-0 rounded-card border border-border bg-card px-[22px] py-5 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-x-5 gap-y-3">
                <div className={eyebrow}>Revenue by property · 12 months</div>
                <div className="flex flex-wrap gap-3.5">
                  {data.rows.map((r) => (
                    <span key={r.id} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-ink-2">
                      <span className="h-[9px] w-[9px] rounded-[3px]" style={{ background: r.chartColor }} />
                      {r.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="h-[240px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly} margin={{ top: 6, right: 4, left: -8, bottom: 0 }} barCategoryGap="30%">
                    <CartesianGrid vertical={false} stroke="var(--sf-line)" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontFamily: "var(--sf-font-mono)", fontSize: 9.5, fill: "var(--sf-muted)" }} />
                    <YAxis tickLine={false} axisLine={false} width={42} tick={{ fontFamily: "var(--sf-font-mono)", fontSize: 10, fill: "var(--sf-muted)" }} tickFormatter={(v: number) => (v === 0 ? "0" : `€${Math.round(v / 1000)}k`)} />
                    {data.rows.map((r, i) => (
                      <Bar key={r.id} dataKey={r.id} stackId="rev" fill={r.chartColor} radius={i === data.rows.length - 1 ? [2, 2, 0, 0] : undefined} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {data.arrivals.length > 0 && (
            <div className="min-w-0 rounded-card border border-border bg-card p-5 shadow-sm">
              <div className="mb-3.5 flex items-center justify-between">
                <span className={eyebrow}>Upcoming arrivals</span>
                <span className="text-[11px] font-semibold text-brand-strong">Next 10 days</span>
              </div>
              <div className="flex flex-col">
                {data.arrivals.map((a, i) => (
                  <div key={`${a.propertyId}-${a.guest}-${i}`} className="flex items-center gap-3 border-t border-line-soft py-3 first:border-t-0">
                    <div className="w-11 flex-none text-center">
                      <div className="font-display text-[16px] font-bold leading-none text-foreground">{a.day}</div>
                      <div className="text-[9.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{a.mon}</div>
                    </div>
                    <span className="h-[34px] w-[2px] flex-none rounded-[2px] bg-brand" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{a.guest}</div>
                      <div className="truncate text-[11.5px] font-medium text-muted-foreground">
                        {a.propertyName}{a.detail ? ` · ${a.detail}` : ""}
                      </div>
                    </div>
                    {a.channel && (
                      <span className="flex-none rounded-full bg-line-soft px-[7px] py-0.5 text-[10px] font-semibold text-ink-2">{a.channel}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function PropertyRowCard({ row, onOpen }: { row: PortfolioRow; onOpen: () => void }) {
  const s = STATUS_STYLE[row.status]
  return (
    <article className="relative overflow-hidden rounded-card border border-border bg-card shadow-sm transition-all hover:border-line-2 hover:shadow-md">
      <span className={cn("absolute bottom-0 left-0 top-0 w-1", s ? s.stripe : "bg-line-2")} />
      <div className="flex flex-wrap items-center gap-4 py-4 pl-[22px] pr-5">
        {row.coverImageUrl ? (
          <img src={row.coverImageUrl} alt="" className="h-12 w-12 flex-none rounded-[13px] object-cover" />
        ) : (
          <span className={cn("flex h-12 w-12 flex-none items-center justify-center rounded-[13px] font-display text-[19px] font-bold text-white", row.avatarClass)}>
            {row.initial}
          </span>
        )}
        <div className="min-w-[160px] flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[15.5px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{row.name}</span>
            {s && (
              <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.03em]", s.pillText, s.pillBg)}>
                {s.label}
              </span>
            )}
            {row.pmsLabel && (
              <span className="rounded-full bg-line-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.03em] text-ink-2">
                {row.pmsLabel}
              </span>
            )}
          </div>
          <div className="mt-[3px] text-[12.5px] font-medium text-muted-foreground">
            {[row.address, row.nextArrivalLabel].filter(Boolean).join(" · ") || " "}
          </div>
        </div>
        <div className="flex flex-none items-center gap-6">
          <Metric label="Pace">
            {row.paceDeltaPct != null ? (
              <span className={cn("inline-flex items-center gap-[3px] font-display text-[15px] font-bold", row.paceDeltaPct >= 0 ? "text-brand-strong" : "text-rose-strong")}>
                {row.paceDeltaPct >= 0 ? <TrendingUp className="h-3 w-3" strokeWidth={2.6} /> : <TrendingDown className="h-3 w-3" strokeWidth={2.6} />}
                {Math.abs(row.paceDeltaPct)}%
              </span>
            ) : (
              <Pending />
            )}
          </Metric>
          <Metric label="Occupancy">
            {row.occupancyPct != null ? <span className="font-display text-[15px] font-bold text-foreground">{row.occupancyPct}%</span> : <Pending />}
          </Metric>
          <Metric label="Reputation">
            {row.reputation != null ? <span className="font-display text-[15px] font-bold text-foreground">{row.reputation}</span> : <Pending />}
          </Metric>
          <Metric label="Actions" center>
            <span className={cn("font-display text-[15px] font-bold", row.openActions > 0 ? "text-rose-strong" : "text-muted-foreground")}>
              {row.openActions}
            </span>
          </Metric>
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border border-border bg-card px-3.5 py-2.5 text-[12.5px] font-semibold text-foreground transition-all hover:border-foreground hover:bg-foreground hover:text-white"
          >
            Open
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </article>
  )
}

function Pending() {
  return <span className="font-display text-[15px] font-bold text-muted-foreground/50">—</span>
}

function Metric({ label, children, center }: { label: string; children: React.ReactNode; center?: boolean }) {
  return (
    <div className={center ? "text-center" : "text-right"}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">{label}</div>
      <div className="mt-[3px]">{children}</div>
    </div>
  )
}
